// app/frontend/components/Maps/EventSchema.tsx
import React from 'react';
import PropTypes from 'prop-types';
import { Head } from '@inertiajs/react';

/**
 * EventSchema - Generates JSON-LD schema markup for an event
 *
 * This component should be included in your event detail page layout.
 * Pass your event data as props to generate the appropriate schema.
 */
const EventSchema = ({
  name,
  description,
  startDate,
  endDate,
  location,
  image,
  offers,
  performer,
  organizer,
  eventStatus = 'https://schema.org/EventScheduled',
  eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode',
  previousStartDate = null,
}) => {
  // Build the schema data object
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name,
    description,
    startDate,
    endDate,
    eventStatus,
    eventAttendanceMode,
  };

  // Add location based on mode (online, offline, or mixed)
  if (eventAttendanceMode === 'https://schema.org/OnlineEventAttendanceMode') {
    schemaData.location = {
      '@type': 'VirtualLocation',
      url: location.url,
    };
  } else if (eventAttendanceMode === 'https://schema.org/OfflineEventAttendanceMode') {
    schemaData.location = {
      '@type': 'Place',
      name: location.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: location.address.streetAddress,
        addressLocality: location.address.addressLocality,
        postalCode: location.address.postalCode,
        addressRegion: location.address.addressRegion,
        addressCountry: location.address.addressCountry,
      },
    };
  } else if (eventAttendanceMode === 'https://schema.org/MixedEventAttendanceMode') {
    schemaData.location = [
      {
        '@type': 'VirtualLocation',
        url: location.virtualUrl,
      },
      {
        '@type': 'Place',
        name: location.name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: location.address.streetAddress,
          addressLocality: location.address.addressLocality,
          postalCode: location.address.postalCode,
          addressRegion: location.address.addressRegion,
          addressCountry: location.address.addressCountry,
        },
      },
    ];
  }

  // Add previous start date if event was rescheduled
  if (previousStartDate && eventStatus === 'https://schema.org/EventRescheduled') {
    schemaData.previousStartDate = previousStartDate;
  }

  // Add image(s)
  if (image) {
    schemaData.image = Array.isArray(image) ? image : [image];
  }

  // Add offers/ticket information
  if (offers) {
    schemaData.offers = {
      '@type': 'Offer',
      url: offers.url,
      price: offers.price,
      priceCurrency: offers.priceCurrency,
      availability: offers.availability || 'https://schema.org/InStock',
      validFrom: offers.validFrom,
    };
  }

  // Add performer information
  if (performer) {
    schemaData.performer = {
      '@type': performer.type || 'PerformingGroup',
      name: performer.name,
    };
  }

  // Add organizer information
  if (organizer) {
    schemaData.organizer = {
      '@type': 'Organization',
      name: organizer.name,
      url: organizer.url,
    };
  }

  return (
    <Head>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Head>
  );
};

EventSchema.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  startDate: PropTypes.string.isRequired, // ISO-8601 format (e.g., "2025-07-21T19:00-05:00")
  endDate: PropTypes.string,
  location: PropTypes.object.isRequired,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  offers: PropTypes.shape({
    url: PropTypes.string,
    price: PropTypes.number,
    priceCurrency: PropTypes.string,
    availability: PropTypes.string,
    validFrom: PropTypes.string,
  }),
  performer: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
  }),
  organizer: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }),
  eventStatus: PropTypes.string,
  eventAttendanceMode: PropTypes.string,
  previousStartDate: PropTypes.string,
};

export default EventSchema;
